import { difference } from 'es-toolkit/compat';
import { Flooding, RelatedFlooding } from 'features/flooding/flooding.types';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type UseFloodingParentFormFieldChangesProps = {
  flooding: Flooding;
};

type FormValues = {
  childFloodings: RelatedFlooding[];
  parentFlooding: RelatedFlooding | null;
};

type ChangeKey = keyof FormValues;

type ChangeMap = Partial<Record<ChangeKey, string>>;

export const useFloodingParentFormFieldChanges = (
  props: UseFloodingParentFormFieldChangesProps,
): ChangeMap => {
  const { flooding } = props;
  const { t } = useTranslation();

  const [childFloodings, parentFlooding] = useWatch<FormValues>({
    name: ['childFloodings', 'parentFlooding'],
  }) as [RelatedFlooding[], RelatedFlooding | null];

  const initialChildFloodings = flooding.childGeoEvents.filter(
    (cge) => cge.geoEventType === 'flooding',
  );

  const addedChildFloodings = difference(
    childFloodings.map((cw) => cw.id),
    initialChildFloodings.map((cw) => cw.id),
  );

  const removedChildFloodings = difference(
    initialChildFloodings.map((cw) => cw.id),
    childFloodings.map((cw) => cw.id),
  );

  const childFloodingsChanged =
    addedChildFloodings.length > 0 || removedChildFloodings.length > 0;

  const initialParentId = flooding.parentGeoEvents?.[0]?.id ?? null;
  const currentParentId = parentFlooding?.id ?? null;
  const parentFloodingChanged = initialParentId !== currentParentId;

  const changes: ChangeMap = {};
  if (childFloodingsChanged) {
    changes.childFloodings = t('geoEventChangelog.childFloodings');
  }
  if (parentFloodingChanged) {
    changes.parentFlooding = t('geoEventChangelog.parentFlooding');
  }

  return changes;
};
